<template>
    <div>
        <div v-if="referralData[referralType].noData" class="no-analytic-data">
            {{ $t(`pages.referrals.reports.no_${referralType}_data`) }}
        </div>
        <div v-else class="reports">
            <div class="bar-chart-container">
                <div>
                    <transparent-select-field v-model="barChartFilter" :options="barCharFilterOptions"></transparent-select-field>
                </div>
                <div class="bar-chart-wrapper">
                    <apexchart
                        type="bar"
                        height="250"
                        :options="getBarChartOptions(barChartFilter)"
                        :series="getBarChartSeries(barChartFilter)"
                    ></apexchart>
                </div>
                <div class="bar-legend">
                    <div
                        v-for="(legendEntry, legendIndex) in getBarChartLegendEntries(barChartFilter)"
                        :key="legendIndex"
                        class="legend-entry bar-legend-entry"
                        @mouseenter="chartLegendEntryHover($event, legendIndex, 'bar')"
                        @mouseleave="chartLegendEntryHover($event, legendIndex, 'bar')"
                    >
                        <div class="legend-entry-circle bar-legend-entry-circle">&nbsp;</div>
                        <div>{{ legendEntry }}</div>
                    </div>
                </div>
            </div>
            <div class="radial-chart-container">
                <div>
                    <transparent-filter-field
                        v-model="radialFilter"
                        class="radial-chart-filter"
                        :options="radialFilterOptions"
                    ></transparent-filter-field>
                </div>
                <div class="radial-chart-wrapper">
                    <div class="radial-chart-holder">
                        <apexchart
                            type="radialBar"
                            height="100%"
                            :options="getRadialChartOptions()"
                            :series="getRadialChartSeries()"
                        ></apexchart>
                    </div>
                </div>
                <div class="radial-legend">
                    <div
                        v-for="(legendEntry, legendIndex) in getRadialChartLegendEntries()"
                        :key="legendIndex"
                        :title="legendEntry.title"
                        class="legend-entry radial-legend-entry"
                        @mouseenter="chartLegendEntryHover($event, legendIndex, 'radial')"
                        @mouseleave="chartLegendEntryHover($event, legendIndex, 'radial')"
                    >
                        <table>
                            <tr>
                                <td><div :class="radialChartLegendEntryClass(legendEntry.key)"></div></td>
                                <td>
                                    <span class="radial-legend-entry-value">{{ legendEntry.value }}</span>
                                </td>
                            </tr>
                            <tr>
                                <td>&nbsp;</td>
                                <td>
                                    <span class="radial-legend-entry-percent">{{ legendEntry.percent }}%</span>
                                </td>
                            </tr>
                        </table>
                    </div>
                </div>
            </div>
            <div class="pie-chart-container">
                <div>
                    <transparent-select-field
                        v-model="pieChartFilter"
                        :options="pieChartFilterOptions"
                    ></transparent-select-field>
                </div>
                <div class="pie-chart-wrapper">
                    <apexchart
                        type="donut"
                        height="250"
                        :options="getPieChartOptions(pieChartFilter)"
                        :series="getPieChartSeries(pieChartFilter)"
                    ></apexchart>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import TransparentSelectField from 'components/report-elements/TransparentSelectField'
    import TransparentFilterField from 'components/report-elements/TransparentFilterField'

    const colors = {
        pending: '#EF752F',
        seen_completed: '#50B43E',
        cancelled_no_show: '#EC3342',
        confirmed: '#4FB1DE',
    }
    export default {
        components: { TransparentFilterField, TransparentSelectField },
        props: {
            referralType: { type: String, required: true },
            referralData: { type: Object, required: true },
        },
        data() {
            return {
                barChartFilter: 'locations',
                barCharFilterOptions: ['locations', 'physicians'].map((v) => ({
                    label: this.$t(`pages.referrals.reports.barchart.${v}`),
                    value: v,
                })),
                radialFilter: null,
                pieChartFilter: 'insurance',
                pieChartFilterOptions: ['insurance', 'diagnosis', 'age', 'referrer'].map((v) => ({
                    label: this.$t(`pages.referrals.reports.piechart.${v}`),
                    value: v,
                })),
                defaultBarChartOptions: {
                    chart: {
                        type: 'bar',
                    },
                    plotOptions: {
                        bar: {
                            borderRadius: 4,
                            horizontal: true,
                            barHeight: '40%',
                        },
                    },
                    colors: [colors.confirmed],
                    dataLabels: {
                        enabled: true,
                    },
                    xaxis: {
                        categories: [],
                        labels: {
                            show: true,
                            trim: false,
                            hideOverlappingLabels: false,
                            style: {
                                fontSize: '16px',
                                fontFamily: 'unset',
                            },
                        },
                    },
                    yaxis: {
                        labels: {
                            style: {
                                fontSize: '16px',
                                fontFamily: 'unset',
                            },
                        },
                    },
                },
                defaultRadialChartOptions: {
                    chart: {
                        type: 'radialBar',
                        redrawOnParentResize: false,
                        redrawOnWindowResize: false,
                        height: '100%',
                    },
                    plotOptions: {
                        radialBar: {
                            inverseOrder: true,
                            offsetY: 0,
                            startAngle: -180,
                            endAngle: 90,
                            hollow: {
                                margin: 5,
                                size: '25%',
                                background: 'transparent',
                            },
                            dataLabels: {
                                value: {
                                    show: false,
                                },
                            },
                        },
                    },
                    legend: {
                        show: true,
                        floating: true,
                        fontSize: '16px',
                        fontFamily: 'unset',
                        position: 'left',
                        offsetY: 100,
                        offsetX: 160,
                        labels: {
                            useSeriesColors: true,
                        },
                        itemMargin: {
                            vertical: -1,
                        },
                        markers: {
                            width: 14,
                            height: 14,
                            radius: 14,
                        },
                    },
                    responsive: [
                        {
                            breakpoint: 480,
                            options: {
                                legend: {
                                    show: false,
                                },
                            },
                        },
                    ],
                },
                defaultPieChartOptions: {
                    chart: {
                        type: 'donut',
                    },
                    dataLabels: {
                        enabled: true,
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    show: true,
                                    name: {
                                        show: false,
                                    },
                                    value: {
                                        show: true,
                                        fontSize: '16px',
                                        fontFamily: 'unset',
                                    },
                                    total: {
                                        show: true,
                                        fontSize: '16px',
                                        fontFamily: 'unset',
                                        label: '',
                                    },
                                },
                            },
                        },
                    },
                    legend: {
                        position: 'top',
                        floating: true,
                        offsetY: 250,
                        fontSize: '16px',
                        fontFamily: 'unset',
                        markers: {
                            width: 14,
                            height: 14,
                            radius: 14,
                        },
                        labels: {
                            useSeriesColors: true,
                        },
                    },
                    responsive: [
                        {
                            breakpoint: 480,
                            options: {
                                chart: {
                                    width: 200,
                                },
                                legend: {
                                    position: 'bottom',
                                },
                            },
                        },
                    ],
                },
            }
        },
        computed: {
            radialFilterOptions() {
                return Object.keys(this.referralData[this.referralType]['referrals']).map((key) => ({
                    label: this.getStatusLabel(key),
                    value: key,
                    class: key,
                }))
            },
            radialFilterValue() {
                return this.radialFilter || Object.keys(this.referralData[this.referralType]['referrals'])
            },
        },
        methods: {
            getStatusLabel(key) {
                return this.$t(`pages.referrals.charts.radial_bar.referral_status.${key}`)
            },
            getBarChartOptions(seriesName) {
                const options = JSON.parse(JSON.stringify(this.defaultBarChartOptions))
                const data = this.referralData[this.referralType][seriesName]
                options.xaxis.categories = Object.keys(data)
                return options
            },
            getRadialChartOptions() {
                const options = JSON.parse(JSON.stringify(this.defaultRadialChartOptions))
                const data = this.radialFilterValue
                options.labels = data.map(this.getStatusLabel)
                options.colors = data.map((key) => colors[key])
                return options
            },
            getPieChartOptions(seriesName) {
                const options = JSON.parse(JSON.stringify(this.defaultPieChartOptions))
                options.labels = Object.keys(this.referralData[this.referralType][seriesName])
                return options
            },
            getBarChartSeries(seriesName) {
                const data = this.referralData[this.referralType][seriesName]
                return [{ data: Object.values(data), name: '' }]
            },
            getRadialChartSeries() {
                const data = []
                Object.entries(this.referralData[this.referralType]['referrals']).forEach((values) => {
                    if (this.radialFilterValue.includes(values[0])) {
                        data.push(values[1])
                    }
                })
                const sum = data.reduce((p, c) => p + c, 0)
                return data.map((v) => Math.round((v * 10000) / sum) / 100)
            },
            getBarChartLegendEntries(seriesName) {
                return Object.keys(this.referralData[this.referralType][seriesName])
            },
            getRadialChartLegendEntries() {
                const data = this.referralData[this.referralType]['referrals']
                const series = this.getRadialChartSeries()
                return this.radialFilterValue.map((key, index) => ({
                    key: key,
                    value: data[key],
                    percent: series[index],
                    title: this.getStatusLabel(key),
                }))
            },
            radialChartLegendEntryClass(key) {
                return {
                    'legend-entry-circle': true,
                    'radial-legend-entry-circle': true,
                    [`radial-legend-entry-circle__${key}`]: true,
                }
            },
            barChartSeriesElements(wrapper, index) {
                wrapper = wrapper.getElementsByClassName('apexcharts-series')[0]
                const elements = Array.from(wrapper.getElementsByTagName('path'))
                elements.splice(index, 1)
                return elements
            },
            radialChartSeriesElements(wrapper, index) {
                return Array.from(wrapper.getElementsByClassName('apexcharts-series'))
                    .concat(Array.from(wrapper.getElementsByClassName('apexcharts-legend-series')))
                    .filter((item) => Number(item.attributes.getNamedItem('rel').value) !== index + 1)
            },
            chartLegendEntryHover(event, index, chartType) {
                const eventType = event.type
                const className = 'legend-mouseover-inactive'
                if (!['mouseenter', 'mouseleave'].includes(eventType)) {
                    return
                }
                const action = eventType === 'mouseenter' ? 'add' : 'remove'
                const wrapper = this.$el.getElementsByClassName(`${chartType}-chart-wrapper`)[0]
                this[`${chartType}ChartSeriesElements`](wrapper, index).forEach((item) => item.classList[action](className))
            },
            getPieChartSeries(seriesName) {
                return Object.values(this.referralData[this.referralType][seriesName])
            },
        },
        watch: {
            referralType() {
                this.radialFilter = null
            },
        },
    }
</script>

<style lang="scss">
    .reports {
        display: flex;
        flex-wrap: wrap;

        > div {
            min-width: 400px;
            max-width: 33%;
            min-height: 300px;
            flex-grow: 1;

            display: flex;
            flex-direction: column;
        }

        .legend-entry {
            cursor: pointer;

            &-circle {
                width: 14px;
                height: 14px;
                border-radius: 50%;
                margin: auto 7px;
            }
        }

        .bar-legend {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;

            &-entry {
                display: flex;
                align-items: center;

                &-circle {
                    background: #4fb1de;
                }
            }
        }

        .radial-chart-wrapper {
            height: 250px;
            display: flex;
            justify-content: center;

            .radial-chart-holder {
                height: 100%;
                min-width: 380px;
            }
        }
        .radial-legend {
            display: flex;
            > div {
                flex-grow: 1;

                > table {
                    margin: 0 auto;
                }
            }

            &-entry-value {
                font-weight: bold;
            }

            &-entry-percent {
                font-size: 14px;
                color: #7c7c7c;
                font-weight: lighter;
            }

            &-entry-circle {
                &__pending {
                    background: #ef752f;
                }

                &__seen_completed {
                    background: #50b43e;
                }

                &__cancelled_no_show {
                    background: #ec3342;
                }

                &__confirmed {
                    background: #4fb1de !important;
                }
            }
        }
        .radial-chart-filter {
            .form-input-toggle.pending i.icon {
                color: #ef752f !important;
            }

            .form-input-toggle.seen_completed i.icon {
                color: #50b43e !important;
            }

            .form-input-toggle.cancelled_no_show i.icon {
                color: #ec3342 !important;
            }

            .form-input-toggle.confirmed i.icon {
                color: #4fb1de !important;
            }
        }
    }

    .no-analytic-data {
        margin-top: 2em;
        text-align: center;
        font-size: 16px;
    }
</style>
